import { useEffect, useState } from "react";

// Sub-components
import { Plus } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import MilestoneGantt from "../../components/shared/MilestoneGantt";
// Shared Components
import PageHeader from "../../components/shared/PageHeader";
import Pagination from "../../components/shared/Pagination";
import SearchInput from "../../components/shared/SearchInput";
import { Button } from "../../components/ui/button.jsx";
import { Label } from "../../components/ui/label.jsx";
import { Switch } from "../../components/ui/switch.jsx";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs.jsx";
// Auth & Hooks
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";

import TaskCreateDialog from "./component/TaskCreateDialog";
import TaskStats from "./component/TaskStats";
import TaskTable from "./component/TaskTable";
import { useTaskApi } from "./hooks/useTaskApi";

const isTaskOverdue = (dueDate, status) => {
  if (!dueDate || status === "COMPLETED" || status === "cancelled") return false;
  return new Date(dueDate) < new Date();
};

const Tasks = () => {
  const { projects, users, globalProjectId, setGlobalProjectId } = useData();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const [viewMode, setViewMode] = useState("LIST");
  const taskApi = useTaskApi(10, globalProjectId === "ALL" ? "" : globalProjectId);

  const {
    tasks,
    pagination,
    setSearchTerm,
    selectedProject,
    setSelectedProject,
    statusFilter,
    setStatusFilter,
    milestoneFilter,
    setMilestoneFilter,
    statusCounts,
    createTask,
    updateTask,
    deleteTask,
    updateTaskStatus,
    getTaskById,
    sortBy,
    setSortBy,
    direction,
    setDirection,
    showDeleted,
    setShowDeleted
  } = taskApi;

  const { setCurrentPage } = pagination;

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [ganttViewMode, setGanttViewMode] = useState("DAILY");
  const overdueCount = Number(statusCounts?.OVERDUE || 0);

  const [permissions, setPermissions] = useState({
    canCreate: false,
    canDelete: false,
    canReassign: false,
    canEdit: false
  });

  const [newTask, setNewTask] = useState({
    projectId: "",
    title: "",
    description: "",
    assignedTo: user?.id || "",
    startDate: "",
    dueDate: "",
    priority: "medium",
    category: "",
    estimatedHours: "",
    status: "PENDING",
    milestoneId: ""
  });

  useEffect(() => {
    if (user?.permissions?.task) {
      setPermissions({
        canCreate: user.permissions.task.create,
        canDelete: user.permissions.task.delete,
        canEdit: user.permissions.task.update,
        canReassign: user.permissions.task.update
      });
    }
  }, [user]);

  // Sync state with search params
  useEffect(() => {
    const taskFilterParam = searchParams.get("filter");
    const milestoneFilterParam = searchParams.get("milestoneId");
    const editTaskIdParam = searchParams.get("editTaskId");

    if (taskFilterParam !== null && taskFilterParam !== statusFilter) {
      setStatusFilter(taskFilterParam);
    }

    if (milestoneFilterParam !== null && milestoneFilterParam !== milestoneFilter) {
      setMilestoneFilter(milestoneFilterParam);
    }

    if (editTaskIdParam) {
      handleEditTask(editTaskIdParam);
      setSearchParams(
        (prev) => {
          prev.delete("editTaskId");
          return prev;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  const handleCreateOrUpdateTask = async (e) => {
    e.preventDefault();

    const requiredFields = [
      { key: "projectId", label: "Project" },
      { key: "title", label: "Task Title" },
      { key: "description", label: "Description" },
      { key: "assignedTo", label: "Assigned To" },
      { key: "startDate", label: "Start Date" },
      { key: "dueDate", label: "Due Date" },
      { key: "priority", label: "Priority" },
      { key: "category", label: "Category" },
      { key: "estimatedHours", label: "Estimated Hours" },
      { key: "status", label: "Status" },
      { key: "milestoneId", label: "Milestone" }
    ];

    const missingFields = requiredFields.filter((f) => !newTask[f.key]);
    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.map((f) => f.label).join(", ")}`);
      return;
    }

    try {
      const payload = {
        title: newTask.title,
        description: newTask.description,
        projectId: newTask.projectId,
        assignedTo: newTask.assignedTo,
        priority: newTask.priority?.toUpperCase(),
        category: newTask.category,
        dueDate: newTask.dueDate ? new Date(newTask.dueDate).toISOString() : null,
        startDate: newTask.startDate ? new Date(newTask.startDate).toISOString() : null,
        estimatedHours: newTask.estimatedHours ? parseFloat(newTask.estimatedHours) : null,
        status: newTask.status?.toUpperCase() || "PENDING",
        milestoneId: newTask.milestoneId || null
      };

      if (editingTaskId) {
        await updateTask(editingTaskId, payload);
      } else {
        await createTask(payload);
      }

      resetForm();
      setIsCreateOpen(false);
    } catch (error) {
      // Error handled in hook toast
    }
  };

  const resetForm = () => {
    setNewTask({
      projectId: globalProjectId && globalProjectId !== "ALL" ? globalProjectId : "",
      title: "",
      description: "",
      assignedTo: user?.id || "",
      startDate: "",
      dueDate: "",
      priority: "medium",
      category: "",
      estimatedHours: "",
      status: "PENDING",
      milestoneId: ""
    });
    setEditingTaskId(null);
  };

  const handleEditTask = async (taskId) => {
    try {
      const taskData = await getTaskById(taskId);
      const formattedStartDate = taskData.startDate ? new Date(taskData.startDate).toISOString().split("T")[0] : "";
      const formattedDueDate = taskData.dueDate ? new Date(taskData.dueDate).toISOString().split("T")[0] : "";

      setNewTask({
        projectId: taskData.project?.id || "",
        title: taskData.title || "",
        description: taskData.description || "",
        assignedTo: taskData.assignedTo?.id || "",
        startDate: formattedStartDate,
        dueDate: formattedDueDate,
        priority: taskData.priority ? taskData.priority.toLowerCase() : "medium",
        category: taskData.category || "",
        estimatedHours: taskData.estimatedHours ? String(taskData.estimatedHours) : "",
        status: taskData.status || "PENDING",
        milestoneId: taskData.milestone?.id || ""
      });
      setEditingTaskId(taskId);
      setIsCreateOpen(true);
    } catch (error) {
      // Error handled in hook toast
    }
  };

  const handleFilterChange = (type, value) => {
    setCurrentPage(0);
    if (type === "status") {
      setStatusFilter(value);
      setSearchParams((prev) => {
        if (value !== "ALL") prev.set("filter", value);
        else prev.delete("filter");
        return prev;
      });
    } else if (type === "milestone") {
      setMilestoneFilter(value);
      setSearchParams((prev) => {
        if (value !== "ALL") prev.set("milestoneId", value);
        else prev.delete("milestoneId");
        return prev;
      });
    } else if (type === "sortBy") {
      setSortBy(value);
    } else if (type === "direction") {
      setDirection(value);
    }
  };

  const filteredTasks = tasks; // Filtering now handled by API

  return (
    <div className="flex-1 flex flex-col animate-in fade-in duration-500">
      <div className="flex-1 space-y-6">
        <PageHeader
          title="Task Management"
          subtitle="Assign and track construction tasks"
          notificationCount={overdueCount}
        />

        {permissions.canCreate && (
          <TaskCreateDialog
            isOpen={isCreateOpen}
            onOpenChange={(open) => {
              setIsCreateOpen(open);
              if (!open) resetForm();
            }}
            editingTaskId={editingTaskId}
            newTask={newTask}
            setNewTask={setNewTask}
            onSubmit={handleCreateOrUpdateTask}
          />
        )}

        <TaskStats
          statusCounts={statusCounts}
          onFilterClick={(f) => {
            setStatusFilter(f);
            setSearchParams((prev) => {
              if (f !== "ALL") prev.set("filter", f);
              else prev.delete("filter");
              return prev;
            });
          }}
        />

        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <Tabs value={viewMode} onValueChange={setViewMode} className="hidden md:block md:w-[200px]">
            <TabsList className="grid w-full grid-cols-2 items-stretch rounded-xl bg-slate-100/50 dark:bg-yale-blue-800/50">
              <TabsTrigger value="LIST" className="text-xs rounded-lg py-2">
                List
              </TabsTrigger>
              <TabsTrigger value="GANTT" className="text-xs rounded-lg py-2">
                Gantt
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex items-center gap-3 w-full md:w-auto md:ml-auto">
            <SearchInput
              placeholder="Search tasks..."
              className="flex-1 md:w-72"
              onSearch={(term) => {
                setCurrentPage(0);
                setSearchTerm(term);
              }}
            />

            {permissions.canCreate && (
              <Button
                onClick={() => {
                  resetForm();
                  setIsCreateOpen(true);
                }}
                className="h-10 w-10 shrink-0"
                aria-label="Create Task"
                title="Create Task"
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {viewMode === "GANTT" ? (
          <div className="space-y-4">
            <MilestoneGantt viewMode={ganttViewMode} onViewModeChange={setGanttViewMode} />
          </div>
        ) : (
          <TaskTable
            tasks={filteredTasks}
            permissions={permissions}
            filters={{
              status: statusFilter,
              milestone: milestoneFilter,
              projectId: globalProjectId,
              sortBy,
              direction
            }}
            onFilterChange={handleFilterChange}
            onEdit={handleEditTask}
            onDelete={deleteTask}
            onStatusUpdate={updateTaskStatus}
            showDeleted={showDeleted}
            setShowDeleted={setShowDeleted}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>

      <div className="mt-8">
        <Pagination pagination={pagination} />
      </div>
    </div>
  );
};

export default Tasks;
